import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, CheckCircle2, FileText, MapPin, Loader2, Info } from 'lucide-react';
import { sendFormEmail } from '../utils/sendFormEmail';
import { getZoneForPosition } from '../data/deliveryZones';
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  if (result.zone === 0) {
    return {
      zone: null as number | null,
      fee: 0
    }; // Custom pricing needed
  }
  return {
    zone: result.zone,
    fee: result.fee
  };
}
interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function QuoteModal({
  isOpen,
  onClose
}: QuoteModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [addressError, setAddressError] = useState('');
  const [isCalculatingDelivery, setIsCalculatingDelivery] = useState(false);
  const [deliveryZone, setDeliveryZone] = useState<number | null>(null);
  const [deliveryFee, setDeliveryFee] = useState(0);
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim()) {
      setDeliveryZone(null);
      setDeliveryFee(0);
      setAddressError('');
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          setDeliveryZone(null);
          setDeliveryFee(0);
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryZone(zoneInfo.zone);
          setDeliveryFee(zoneInfo.fee);
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        setDeliveryZone(null);
        setDeliveryFee(0);
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const inputs = form.querySelectorAll('input');
    const selects = form.querySelectorAll('select');
    const textarea = form.querySelector('textarea');
    const customerName = (inputs[0] as HTMLInputElement)?.value || '';
    const customerEmail = (inputs[2] as HTMLInputElement)?.value || '';
    await sendFormEmail({
      subject: '253Gravel — New Quote Request',
      'Full Name': customerName,
      Phone: (inputs[1] as HTMLInputElement)?.value || '',
      email: customerEmail,
      _replyto: customerEmail,
      _autoresponse: `Hi ${customerName}! Thank you for requesting a quote from 253 Gravel! We've received your details and will get back to you shortly with pricing and availability.\n\nIf you need anything sooner, call us at (206) 619-3742.\n\n- The 253 Gravel Team`,
      'Delivery Address': addressInput,
      'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone} (+${deliveryFee})` : 'Outside standard zones',
      Material: (selects[0] as HTMLSelectElement)?.value || '',
      'Estimated Yardage': (selects[1] as HTMLSelectElement)?.value || '',
      'Project Details': textarea?.value || ''
    });
    setIsSubmitted(true);
  };
  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setIsSubmitted(false);
      setAddressInput('');
      setDeliveryZone(null);
      setDeliveryFee(0);
    }, 300);
  };
  const inputClass = 'w-full px-3.5 py-2.5 bg-[#f5f2ee] dark:bg-[#1a1918] border border-stone-300 dark:border-[#2a2928] rounded text-brand-charcoal dark:text-stone-200 placeholder-brand-muted dark:placeholder-stone-500 focus:ring-1 focus:ring-brand-orange/50 focus:border-brand-orange/50 transition-all text-sm';
  return <AnimatePresence data-id="element-787">
      {isOpen && <>
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={handleClose} className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60]" data-id="element-788" />

          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 300
      }} className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none" data-id="element-789">
            <div className="w-full max-w-2xl max-h-[90vh] bg-[#e5e1db] dark:bg-[#1e1d1c] shadow-2xl flex flex-col rounded-2xl border border-stone-400 dark:border-[#333231] pointer-events-auto overflow-hidden" data-id="element-790">
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 bg-[#eae6e0] dark:bg-[#232221] border-b border-stone-400 dark:border-[#333231]" data-id="element-791">
                <div className="flex items-center space-x-3" data-id="element-792">
                  <FileText className="w-5 h-5 text-brand-orange" data-id="element-793" />
                  <h2 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white" data-id="element-794">
                    Request a Quote
                  </h2>
                </div>
                <button onClick={handleClose} className="p-1.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-brand-lightgray dark:hover:bg-[#4c4c4c]" data-id="element-795">
                  <X className="w-5 h-5" data-id="element-796" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6" data-id="element-797">
                {isSubmitted ? <div className="flex flex-col items-center justify-center text-center space-y-5 py-8" data-id="element-798">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                damping: 15,
                stiffness: 200
              }} className="w-16 h-16 bg-[#1DB954]/20 dark:bg-[#1DB954]/20 rounded-full flex items-center justify-center" data-id="element-799">
                      <CheckCircle2 className="w-8 h-8 text-[#1DB954] dark:text-[#1DB954]" data-id="element-800" />
                    </motion.div>
                    <div data-id="element-801">
                      <h3 className="font-heading text-xl font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-802">
                        Quote Request Sent!
                      </h3>
                      <p className="text-stone-600 dark:text-stone-200 max-w-xs mx-auto" data-id="element-803">
                        Thanks for reaching out. We'll get back to you shortly
                        with pricing and availability.
                      </p>
                    </div>
                    <button onClick={handleClose} className="bg-brand-charcoal dark:bg-[#4c4c4c] hover:bg-brand-charcoal/90 dark:hover:bg-[#5d5d5d] text-white px-8 py-2.5 rounded-xl font-bold transition-colors" data-id="element-804">
                      Done
                    </button>
                  </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-805">
                    {/* Row 1: Name & Phone */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-806">
                      <div data-id="element-807">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-808">
                          Full Name *
                        </label>
                        <input type="text" required className={inputClass} placeholder="John Doe" autoComplete="name" data-id="element-809" />
                      </div>
                      <div data-id="element-810">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-811">
                          Phone Number *
                        </label>
                        <input type="tel" required className={inputClass} placeholder="(253) 555-0123" autoComplete="tel" data-id="element-812" />
                      </div>
                    </div>

                    {/* Row 2: Email & Delivery Address */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-813">
                      <div data-id="element-814">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-815">
                          Email Address *
                        </label>
                        <input type="email" required className={inputClass} placeholder="john@example.com" autoComplete="email" data-id="element-816" />
                      </div>
                      <div className="md:col-span-2" data-id="element-817">
                        <label className="flex items-center justify-between text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-818">
                          <span className="flex items-center" data-id="element-819">
                            <MapPin className="w-4 h-4 mr-2 text-stone-400" data-id="element-820" />
                            Delivery Address *
                          </span>
                          {isCalculatingDelivery && <span className="text-xs text-brand-orange flex items-center normal-case" data-id="element-821">
                              <Loader2 className="w-3 h-3 animate-spin mr-1" data-id="element-822" />
                              Calculating...
                            </span>}
                        </label>
                        <input type="text" required value={addressInput} onChange={e => setAddressInput(e.target.value)} className={inputClass} placeholder="123 Main St, Tacoma, WA" autoComplete="street-address" data-id="element-823" />
                        {addressError && <p className="text-xs text-red-500 mt-1" data-id="element-824">
                            {addressError}
                          </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-xs text-green-600 dark:text-green-400 mt-1 flex items-center" data-id="element-825">
                              <CheckCircle2 className="w-3 h-3 mr-1" data-id="element-826" />
                              Zone {deliveryZone} (
                              {deliveryFee === 0 ? 'Free Delivery' : `+$${deliveryFee} Delivery`}
                              )
                            </p>}
                        {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-xs text-brand-orange mt-1 flex items-center" data-id="element-827">
                              <Info className="w-3 h-3 mr-1" data-id="element-828" />
                              Outside standard zones. We'll provide a custom
                              delivery quote.
                            </p>}
                      </div>
                    </div>

                    {/* Row 3: Material & Yardage */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-829">
                      <div data-id="element-830">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-831">
                          Material Needed
                        </label>
                        <select className={inputClass} data-id="element-832">
                          <option data-id="element-833">Crushed Rock</option>
                          <option data-id="element-834">Pea Gravel</option>
                          <option data-id="element-835">Drain Rock</option>
                          <option data-id="element-836">Road Base</option>
                          <option data-id="element-837">Sand/Topsoil</option>
                          <option data-id="element-838">Bark & Mulch</option>
                          <option data-id="element-839">Sand</option>
                          <option data-id="element-840">River Rock</option>
                          <option data-id="element-841">Not Sure Yet</option>
                        </select>
                      </div>
                      <div data-id="element-842">
                        <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-843">
                          Estimated Yardage
                        </label>
                        <select className={inputClass} data-id="element-844">
                          <option data-id="element-845">4 Loads</option>
                          <option data-id="element-846">5 Loads</option>
                          <option data-id="element-847">Other</option>
                        </select>
                      </div>
                    </div>

                    {/* Row 4: Project Details */}
                    <div data-id="element-848">
                      <label className="block text-xs font-semibold text-stone-800 dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-849">
                        Project Details (Optional)
                      </label>
                      <textarea rows={3} className={`${inputClass} resize-none`} placeholder="Tell us about your project or any delivery instructions..." data-id="element-850"></textarea>
                    </div>

                    <button type="submit" className="w-full bg-[#3b3b3b] hover:bg-[#2a2a2a] text-white py-3.5 rounded font-bold transition-colors flex items-center justify-center border border-[#4c4c4c]" data-id="element-851">
                      <Send className="w-4 h-4 mr-2" data-id="element-852" />
                      Send Quote Request
                    </button>
                  </form>}
              </div>
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}